import type { Row } from "@msuite/ui/table";
import { moment } from "@msuite/utils/date";
import { guardNumber } from "@msuite/utils/numbers";
import { mergeSettings } from "@msuite/utils/settings";
import type { Nullable, ProjectCardWithInvoices, Tables } from "@msuite/utils/types";
import type { DayUnit } from "@msuite/utils/units";

export interface InvoiceData {
	hasInvoice: boolean;
	type: Nullable<Tables<"outgoingInvoices">["type"]>;

	hasFinalInvoice: boolean;
	hasProgressInvoice: boolean;
	hasSingleInvoice: boolean;

	lastInvoiceCalculatedTo: Nullable<string>;
	nextPossibleInvoice: Nullable<string>;
	daysLeft: Nullable<number>;
}

export function getInvoiceData(row: ProjectCardWithInvoices): InvoiceData | null {
	const response: InvoiceData = {
		hasInvoice: false,
		type: null,

		hasFinalInvoice: false,
		hasProgressInvoice: false,
		hasSingleInvoice: false,

		lastInvoiceCalculatedTo: null,
		nextPossibleInvoice: null,
		daysLeft: null,
	};

	const invoices = row._invoices;

	const settings = mergeSettings<
		Pick<Tables<"projectSettings">, "invoicingPeriodUnit" | "invoicingPeriodValue">
	>(row._organization._organizationSettings, row._projectSettings);

	invoices.sort((a, b) => moment(b.calculatedTo).diff(moment(a.calculatedTo)));

	const hasInvoice = guardNumber(invoices.length) > 0;
	const hasFinalInvoice = invoices.some((invoice) => invoice.type === "final-invoice");
	const hasProgressInvoice = invoices.some((invoice) => invoice.type === "progress-invoice");
	const hasSingleInvoice =
		invoices.some((invoice) => invoice.type === "single-invoice") && !hasProgressInvoice;

	response.hasInvoice = hasInvoice;
	response.hasFinalInvoice = hasFinalInvoice;
	response.hasProgressInvoice = hasProgressInvoice;
	response.hasSingleInvoice = hasSingleInvoice;

	if (hasFinalInvoice) {
		const finalInvoice = invoices.find((invoice) => invoice.type === "final-invoice");
		const calculatedTo = finalInvoice?.calculatedTo;
		response.type = "final-invoice";
		if (calculatedTo) {
			response.lastInvoiceCalculatedTo = calculatedTo;
		}
	} else if (hasSingleInvoice) {
		const singleInvoice = invoices.find((invoice) => invoice.type === "single-invoice");
		const calculatedTo = singleInvoice?.calculatedTo;
		response.type = "single-invoice";
		if (calculatedTo) {
			response.lastInvoiceCalculatedTo = calculatedTo;
		}
	} else if (hasProgressInvoice) {
		const progressInvoice = invoices.find((invoice) => invoice.type === "progress-invoice");
		const calculatedTo = progressInvoice?.calculatedTo;
		response.type = "progress-invoice";
		if (calculatedTo) {
			response.lastInvoiceCalculatedTo = calculatedTo;
			const nextPossibleInvoice = moment(calculatedTo)
				.addUnit(
					guardNumber(settings?.invoicingPeriodValue),
					(settings?.invoicingPeriodUnit || "calendar-days") as DayUnit,
				)
				.db();
			const daysLeft = moment(calculatedTo).diff(moment(), "days");
			response.nextPossibleInvoice = nextPossibleInvoice;
			response.daysLeft = daysLeft;
		}
	}

	return response;
}

export function invoiceDataSortingFn(
	rowA: Row<ProjectCardWithInvoices>,
	rowB: Row<ProjectCardWithInvoices>,
	columnId: string,
) {
	const invoiceDataA = rowA.getValue(columnId) as InvoiceData;
	const invoiceDataB = rowB.getValue(columnId) as InvoiceData;

	if (!invoiceDataA.hasInvoice) return 1;
	if (!invoiceDataB.hasInvoice) return -1;
	if (invoiceDataA.hasProgressInvoice && !invoiceDataB.hasProgressInvoice) return 1;
	if (!invoiceDataA.hasProgressInvoice && invoiceDataB.hasProgressInvoice) return -1;
	if (invoiceDataA.hasFinalInvoice && !invoiceDataB.hasFinalInvoice) return 1;
	if (!invoiceDataA.hasFinalInvoice && invoiceDataB.hasFinalInvoice) return -1;
	if (invoiceDataA.hasSingleInvoice && !invoiceDataB.hasSingleInvoice) return 1;
	if (!invoiceDataA.hasSingleInvoice && invoiceDataB.hasSingleInvoice) return -1;

	return moment(invoiceDataA.nextPossibleInvoice).diff(moment(invoiceDataB.nextPossibleInvoice));
}
